// src/App.js
import React, { useEffect } from "react";
import { Switch, Route, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import { logEvent } from "firebase/analytics";
import { analytics } from "./firebase/firebase";

import Header from "./components/Header/Header";
import Footer from "./components/Footer/Footer";

import Home from "./screens/Home/Home";
import Galeria from "./screens/Galeria/Galeria";
import Shop from "./screens/Shop/Shop";
import Servicios from "./screens/Servicios/Servicios";
import Favoritos from "./screens/Favoritos/Favoritos";
import SobreMi from "./screens/SobreMi/SobreMi";
import Contacto from "./screens/Contacto/Contacto";
import NotFound from "./screens/NotFound/NotFound";

import Abstractas from "./screens/Abstractas/Abstractas";
import AlmasVisibles from "./screens/AlmasVisibles/AlmasVisibles";
import PaisajesUrbanos from "./screens/PaisajesUrbanos/PaisajesUrbanos";
import EntornoNatural from "./screens/EntornoNatural/EntornoNatural";
import Retratos from "./screens/Retratos/Retratos";
import EnMovimiento from "./screens/EnMovimiento/EnMovimiento";
import HistoriasAnonimas from "./screens/HistoriasAnonimas/HistoriasAnonimas";
import Arquitectura from "./screens/Arquitectura/Arquitectura";
import Urbanismo from "./screens/Urbanismo/Urbanismo";
import Paisajes from "./screens/Paisajes/Paisajes";
import Naturaleza from "./screens/Naturaleza/Naturaleza";
import Fauna from "./screens/Fauna/Fauna";

import Libros from "./screens/Libros/Libros";
import Obras from "./screens/Obras/Obras";
import Deporte from "./screens/Deporte/Deporte";
import BooksPersonalizados from "./screens/BooksPersonalizados/BooksPersonalizados";
import Vinilos from "./screens/Vinilos/Vinilos";
import Clases from "./screens/Clases/Clases";

import Carrito from "./screens/Carrito/Carrito";
import Login from "./screens/Login/Login";
import Register from "./screens/Register/Register";
import DetalleCard from "./screens/DetalleCard/DetalleCard";
import DetalleLibros from "./screens/DetalleLibros/DetalleLibros";

import AdminSubscribers from "./screens/AdminSubscribers/AdminSubscribers";
import CopyRight from "./screens/Legal/CopyRight";

import PagoSuccess from "./screens/EstadoPago/PagoSuccess";
import PagoFailure from "./screens/EstadoPago/PagoFailure";
import PagoPending from "./screens/EstadoPago/PagoPending";

import "./app.css";

const SITE = "https://juanipavlovsky.com.ar";

function useGAPageViews() {
  const location = useLocation();
  useEffect(() => {
    if (!analytics) return;
    logEvent(analytics, "page_view", {
      page_title: document.title || undefined,
      page_location: window.location.href,
      page_path: location.pathname + location.search,
    });
    logEvent(analytics, "screen_view", {
      firebase_screen: document.title || location.pathname,
      firebase_screen_class: "AppRoute",
    });
  }, [location.pathname, location.search]);
}

function App() {
  useGAPageViews();
  const location = useLocation();

  // Canonical/OG siempre al dominio final
  const canonical = `${SITE}${location.pathname}${location.search || ""}`;
  const ogImage = `${SITE}/images/og-home.jpg`; // cambialo si querés otra imagen

  // JSON-LD WebSite
  const ldWebsite = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Juani Pavlovsky Fotografías",
    alternateName: ["Fotografías Juani Pavlovsky", "Juani Pavlovsky"],
    url: SITE,
    inLanguage: "es-AR",
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE}/?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };

const ldOrg = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Juani Pavlovsky",
  url: SITE,
  logo: {
    "@type": "ImageObject",
    url: `${SITE}/images/logo-jp.png`,
    width: 512,
    height: 512
  },
  sameAs: [
    "https://www.instagram.com/juanipavlovskyph?igsh=MW4wcngwc2FnODczbQ==", // poné tus cuentas reales
    // "https://www.facebook.com/...", // si tenés
  ]
};

  return (
    <div className="app-container">
      {/* Helmet global: template de títulos, site name, canonical y OG base */}
      <Helmet
        titleTemplate="%s | Juani Pavlovsky"
        defaultTitle="Juani Pavlovsky Fotografías"
      >
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href={canonical} />

        {/* Site name para Google/Twitter/OG */}
        <meta property="og:site_name" content="Juani Pavlovsky Fotografías" />
        <meta name="application-name" content="Juani Pavlovsky Fotografías" />
        <meta name="apple-mobile-web-app-title" content="Juani Pavlovsky Fotografías" />

        {/* Open Graph base */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={ogImage} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={ogImage} />

        {/* Datos estructurados */}
        <script type="application/ld+json">{JSON.stringify(ldWebsite)}</script>
        <script type="application/ld+json">{JSON.stringify(ldOrg)}</script>
      </Helmet>

      <Header />
      <div className="content">
        <Switch>
          <Route exact path="/" component={Home} />
          <Route exact path="/galeria" component={Galeria} />
          <Route exact path="/galeria/abstractas" component={Abstractas} />
          <Route exact path="/galeria/almas-visibles" component={AlmasVisibles} />
          <Route exact path="/galeria/almas-visibles/retratos" component={Retratos} />
          <Route exact path="/galeria/almas-visibles/en-movimiento" component={EnMovimiento} />
          <Route exact path="/galeria/almas-visibles/historias-anonimas" component={HistoriasAnonimas} />
          <Route exact path="/galeria/paisajes-urbanos" component={PaisajesUrbanos} />
          <Route exact path="/galeria/paisajes-urbanos/arquitectura" component={Arquitectura} />
          <Route exact path="/galeria/paisajes-urbanos/urbanismo" component={Urbanismo} />
          <Route exact path="/galeria/entorno-natural" component={EntornoNatural} />
          <Route exact path="/galeria/entorno-natural/paisajes" component={Paisajes} />
          <Route exact path="/galeria/entorno-natural/naturaleza" component={Naturaleza} />
          <Route exact path="/galeria/entorno-natural/fauna" component={Fauna} />

          <Route exact path="/shop" component={Shop} />
          <Route exact path="/shop/libros" component={Libros} />
          <Route exact path="/shop/obras" component={Obras} />

          <Route exact path="/servicios" component={Servicios} />
          <Route exact path="/servicios/deporte" component={Deporte} />
          <Route exact path="/servicios/books-personalizados" component={BooksPersonalizados} />
          <Route exact path="/servicios/vinilos" component={Vinilos} />
          <Route exact path="/servicios/clases" component={Clases} />

          <Route exact path="/favoritos" component={Favoritos} />
          <Route exact path="/sobre-mi" component={SobreMi} />
          <Route exact path="/contacto" component={Contacto} />

          <Route exact path="/login" component={Login} />
          <Route exact path="/register" component={Register} />
          <Route exact path="/carrito" component={Carrito} />

          <Route exact path="/detalle/:id" component={DetalleCard} />
          <Route exact path="/detallelibros/:id" component={DetalleLibros} />

          <Route exact path="/pago/success" component={PagoSuccess} />
          <Route exact path="/pago/failure" component={PagoFailure} />
          <Route exact path="/pago/pending" component={PagoPending} />

          <Route exact path="/admin/subscribers" component={AdminSubscribers} />
          <Route exact path="/legal/copyright" component={CopyRight} />
          <Route component={NotFound} />
        </Switch>
      </div>
      <Footer />
    </div>
  );
}

export default App;
